import {
  ChangeDetectorRef, Component, OnInit, OnDestroy, Input, Output, EventEmitter,
  ViewEncapsulation, ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import { CoreConfigService } from '@core/services/config.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';

import { AppPermissionsService, AuthenticationService, CompanyService } from '@common/services';
import { Company, User } from '@common/models';
import { environment } from 'environments/environment';
import { InsightsService, MSGraphService } from '@core/services';
import { blobToBase64 } from '@core/utils';
import { ChatDockService } from 'app/components/chat-ai/services/chat-dock.service';
import { UploadManagerService } from 'app/services/upload-manager.service';
import { LayoutSwitchService } from 'app/services/layout-switch.service';

@Component({
  selector: 'beliv-header',
  templateUrl: './beliv-header.component.html',
  styleUrls: ['./beliv-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class BelivHeaderComponent implements OnInit, OnDestroy {
  @Input() navbarPinned = true;
  @Output() pinToggle = new EventEmitter<void>();

  currentUser: User;
  currentSkin = 'default';
  prevSkin: string;
  profilePhoto: any;
  canSeeBIX: boolean;
  isDev = !environment.production;

  // Empresas a las que tiene acceso el usuario y la activa (cid)
  companies: Company[] = [];
  selectedCompany: Company;

  selectedLanguage: string;
  languageOptions: Record<string, { title: string; flag: string }> = {
    en: { title: 'English', flag: 'us' },
    es: { title: 'Español', flag: 'es' }
  };

  // Buscador global: lo escrito y si está desplegado (solo en teléfono)
  searchTerm = '';
  searchOpen = false;

  @ViewChild('popoverBix') popoverBix: NgbPopover;

  private _unsubscribeAll = new Subject<any>();

  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _permissionsService: AppPermissionsService,
    private _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreMenuService: CoreMenuService,
    public _translateService: TranslateService,
    private _msGraphService: MSGraphService,
    private _cdr: ChangeDetectorRef,
    private _chatDockService: ChatDockService,
    private _companyService: CompanyService,
    private _insightsService: InsightsService,
    public uploadManagerService: UploadManagerService,
    public layoutSwitch: LayoutSwitchService
  ) {
    this.canSeeBIX = this._permissionsService.hasPermissionByUrl('pages/chat', 'READ');
  }

  // ── Public methods ────────────────────────────────────────────

  toggleSidebar(key: string): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  setLanguage(language: string): void {
    this.selectedLanguage = language;
    this._translateService.use(language);
    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  toggleDarkSkin(): void {
    this.prevSkin = localStorage.getItem('prevSkin');
    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin || 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }

  /** El selector sólo aparece si el usuario tiene acceso a más de una empresa */
  get showCompanySelector(): boolean {
    return this.companies.length > 1;
  }

  /**
   * Cambia la empresa activa: recarga permisos, menús y estados con el nuevo
   * cid y vuelve al inicio, porque la ruta actual puede no existir en la otra
   * empresa. Mismo flujo que el selector del home-header.
   * @param company Empresa elegida
   */
  changeCompany(company: Company): void {
    if (!company || company.id === this._authenticationService.currentCompanyIdValue) return;

    this.log('Cambió de empresa desde el header', {
      fromCompanyId: this._authenticationService.currentCompanyIdValue,
      toCompanyId: company.id,
      toCompanyName: company.name
    });

    this._authenticationService.switchCompany(company.id);
    this._router.navigate(['/']);
  }

  /**
   * Busca en todo el portal, igual que el buscador del home header: lleva a
   * la página de resultados con lo escrito en `q`.
   * @param event Submit del formulario (Enter)
   */
  onSearchSubmit(event: Event): void {
    event.preventDefault();

    const q = (this.searchTerm || '').trim();
    if (!q) return;

    // Solo el largo: lo escrito puede incluir datos personales
    this._insightsService.traceInfo(environment.projects.Search, 'Buscó desde el header', { length: q.length },
      this._authenticationService.currentUserValue);

    this.searchOpen = false;
    this._router.navigate(['/search'], { queryParams: { q } });
  }

  /**
   * Despliega o pliega el buscador en teléfono. Al abrirlo se enfoca el campo
   * para que el teclado aparezca sin otro toque.
   * @param input Campo del buscador
   */
  toggleSearch(input: HTMLInputElement): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) setTimeout(() => input?.focus());
  }

  /**
   * Borra lo escrito y deja el cursor en el campo para buscar otra cosa
   * @param input Campo del buscador
   */
  clearSearch(input: HTMLInputElement): void {
    this.searchTerm = '';
    input?.focus();
  }

  /** Pliega el buscador (Escape) */
  closeSearch(): void {
    this.searchOpen = false;
  }

  onPinToggle(): void {
    this.pinToggle.emit();
  }

  logout(): void {
    this._coreMenuService.unregister('main');
    this._authenticationService.logout();
    this._router.navigate(['/pages/authentication/login']);
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  ngOnInit(): void {
    // User
    this._authenticationService.onUserValueChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(resp => {
        if (resp) {
          this.currentUser = resp;
          if (localStorage.getItem('auth-type') === 'saml') {
            this._msGraphService.register();
          }
          this._loadUserCompanies();
        }
      });

    // Empresa activa: se sincroniza el selector cuando cambia
    this._authenticationService.onCompanyIdChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(resp => {
        if (resp) {
          Promise.resolve().then(() => {
            this._syncSelectedCompany();
            this._cdr.detectChanges();
          });
        }
      });

    // Profile photo via MS Graph
    this._msGraphService.onAccessTokenChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(resp => {
        if (resp) {
          this._msGraphService.getPhoto()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(photo => {
              blobToBase64(photo).then(value => {
                Promise.resolve().then(() => {
                  this.profilePhoto = value;
                  this._cdr.detectChanges();
                });
              });
            });
        }
      });

    // Permissions (BIX)
    this._permissionsService.onPermissionsChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(res => {
        if (res) {
          Promise.resolve().then(() => {
            this.canSeeBIX = this._permissionsService.hasPermissionByUrl('pages/chat', 'READ');
            this._cdr.detectChanges();
          });
        }
      });

    // Config (skin)
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
      });

    // Language
    this.selectedLanguage = this._translateService.currentLang || 'es';
    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(event => {
        this.selectedLanguage = event.lang;
        this._cdr.detectChanges();
      });

  }

  ngAfterViewInit(): void {
    if (this.popoverBix) {
      setTimeout(() => this.popoverBix.open(), 1000);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /** Trae las empresas a las que tiene acceso el usuario */
  private _loadUserCompanies(): void {
    this._companyService.getUserCompanies()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: resp => {
          this.companies = resp || [];
          Promise.resolve().then(() => {
            this._syncSelectedCompany();
            this._cdr.detectChanges();
          });
        },
        error: err => {
          // Sin la traza el selector simplemente no aparecería y no quedaría rastro
          this.log('Error al obtener las empresas del usuario', { status: err?.status }, err, true);
        }
      });
  }

  /** Deja en el selector la empresa activa (cid) */
  private _syncSelectedCompany(): void {
    const currentCompanyId = this._authenticationService.currentCompanyIdValue;

    // Todavía no llegaron la empresa activa o las del usuario: se reintenta
    // cuando emita onCompanyIdChanged
    if (!currentCompanyId || !this.companies.length) return;

    this.selectedCompany = this.companies.find(company => company.id === currentCompanyId);

    // Si la empresa activa ya no pertenece al usuario, se pasa a la primera
    if (!this.selectedCompany) {
      this._authenticationService.switchCompany(this.companies[0].id);
    }
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.Company, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.Company, message, data, user);
  }

  /**
   * Abre o cierra el panel acoplado de Bryant.
   *
   * Antes este botón navegaba a /pages/chat; ahora el chat se despliega al
   * costado y el contenido de la app se encoge para dejarle espacio.
   */
  toggleBryant(): void {
    this._chatDockService.toggle();
  }
}
